// =====================================================================
//  Ambient token effects: what each token's "effect" looks like.
//
//  Every entry is keyed by the effect name from tokens.csv (the "effect"
//  column, or the token's id when that column is blank). An effect is a
//  short list of layers. Change colors, sizes and timings here.
//
//  Sizes: "r" and "spread" are in token widths (1 = one token across);
//  "size" and "height" are in centimeters. "every" is seconds between pops.
//  "grow" (0 to 1) is how much the layer shrinks at low counter values:
//  0 = always the same, 1 = fades to nothing at zero. It only matters for
//  tokens with a full_at value in tokens.csv.
// =====================================================================

export type MoteShape = 'dot' | 'heart' | 'z' | 'drop' | 'bolt' | 'flake' | 'spike' | 'arrow';

export type FxPart =
  /** a soft glow under the token that slowly pulses (and can shift between two colors) */
  | { t: 'aura'; color: string; color2?: string; r: number; opacity: number; speed?: number; grow?: number; solid?: boolean }
  /** a ring that spreads out from the token every few seconds */
  | { t: 'ripple'; color: string; every: number; r: number; opacity: number; grow?: number }
  /** small shapes that rise off the token, sink down onto it, circle it, or slide outward */
  | {
      t: 'motes';
      shape: MoteShape;
      color: string;
      color2?: string;
      count: number;
      path: 'rise' | 'fall' | 'orbit' | 'out';
      size: number;
      spread: number;
      life?: number; // seconds each one lasts (rise / fall / out)
      every?: number; // seconds between waves; leave out for a steady stream
      height?: number; // how high they rise, or how high they start when falling
      speed?: number; // how fast they circle
      trail?: number; // faint copies following each one (a "fast" look)
      solid?: boolean; // draw as solid color instead of light (for dark colors)
      grow?: number;
    }
  /** a ring of light floating above the token */
  | { t: 'halo'; color: string }
  /** a see-through color laid over the card the token is on, optionally with frost or cracks */
  | { t: 'tint'; color: string; min: number; max: number; overlay?: 'frost' | 'cracks'; overlayColor?: string }
  /** a see-through dome over the card the token is on */
  | { t: 'dome'; color: string; min: number; max: number }
  /** little lightning arcs that flicker across the card every few seconds */
  | { t: 'arcs'; color: string; every: number }
  /**
   * quick diagonal slashes of light across the card the token is on (or the token itself), every few seconds.
   * cross: the second wave runs along the other diagonal, the opposite way, so the two make an X.
   * scale: how big the slashes are (1 = the Ambush arrival, 0.7 = smaller) · speed: how fast (1.5 = half as long again as fast)
   * gap: seconds between the first slash and the second (cross only)
   */
  | { t: 'streaks'; color: string; color2?: string; every: number; cross?: boolean; scale?: number; speed?: number; gap?: number }
  /** the token shudders and shows cracks every few seconds */
  | { t: 'jitter'; every: number }
  /** a dark ring that closes in on the token, as if dragging it down */
  | { t: 'pull'; color: string; every: number; r: number };

export interface TokenRecipe {
  color: string; // also the color of the placeholder chip
  parts: FxPart[];
  fly?: boolean; // the card under this token hovers like a flying card
  ground?: boolean; // the card under this token is held on the table, even if it flies
  link?: boolean; // a glowing line joins these tokens in the order they were placed
}

export const TOKEN_RECIPES: Record<string, TokenRecipe> = {
  // ---------------- miscellaneous ----------------
  darkgale: {
    color: '#8c1c2e',
    parts: [
      { t: 'tint', color: '#7a1024', min: 0.3, max: 0.3 },
      { t: 'aura', color: '#ff6a2b', color2: '#c21f1f', r: 1.05, opacity: 0.6, speed: 0.9 },
      { t: 'motes', shape: 'dot', color: '#ff8a3c', color2: '#ffd27a', count: 6, path: 'rise', life: 1.6, size: 0.6, spread: 0.35, height: 2.4 },
    ],
  },
  fractal: { color: '#8fd0ff', parts: [] },
  flying: {
    color: '#cfe9ff',
    fly: true,
    parts: [
      { t: 'aura', color: '#cfe9ff', r: 0.9, opacity: 0.28, speed: 0.3 },
      { t: 'motes', shape: 'dot', color: '#ffffff', count: 3, path: 'rise', life: 3, size: 0.35, spread: 0.8, height: 2 },
    ],
  },
  // steel grey ball and chain, with a dark ring that keeps closing in
  ground: {
    color: '#5c6878',
    ground: true,
    parts: [
      { t: 'aura', color: '#8fa0b3', r: 0.9, opacity: 0.2, speed: 0.25 },
      { t: 'pull', color: '#0e1114', every: 3.5, r: 1.7 },
    ],
  },
  // how a unit deals its damage, put on a summoned unit that differs from the usual (Navik's Skell-Mini: Direct)
  direct: { color: '#b8452f', parts: [] },
  pierce: { color: '#7d4bb3', parts: [] },
  target: { color: '#2f6fb0', parts: [] },
  // The River Keeper's Bell: whoever has it, their opponent discards when its card is destroyed
  bell: { color: '#3b5f63', parts: [{ t: 'aura', color: '#9fe3d8', r: 0.85, opacity: 0.22, speed: 0.3 }] },
  // Gerzite Farmer's Harvest counter: warm gold, with crystal-blue specks drifting up off it like a crop being gathered
  resource: {
    color: '#c9982f',
    parts: [
      { t: 'aura', color: '#ffd76a', color2: '#7fd0ff', r: 1.0, opacity: 0.3, speed: 0.35 },
      { t: 'motes', shape: 'spike', color: '#8fd0ff', color2: '#ffe9a8', count: 3, path: 'rise', life: 2.4, size: 0.5, spread: 0.5, height: 1.8 },
    ],
  },
  // Xolox's Perish Bank: a spectral health counter (it holds health for your Units, it doesn't add any)
  'life-bank': {
    color: '#5a7fd6',
    parts: [
      { t: 'aura', color: '#9ad8ff', color2: '#b49cff', r: 1.1, opacity: 0.32, speed: 0.4 },
      { t: 'motes', shape: 'heart', color: '#c6e8ff', count: 2, path: 'rise', life: 2.8, size: 0.55, spread: 0.6, height: 1.8 },
    ],
  },
  lanespanner: { color: '#5fe0d6', link: true, parts: [{ t: 'aura', color: '#5fe0d6', r: 0.85, opacity: 0.35, speed: 0.35 }] },
  // two Link counters with the same number link their cards: a green line joins each pair (green, so they
  // can't be mistaken for the gold Range counter)
  link: { color: '#2f9e4f', parts: [{ t: 'aura', color: '#5fe08a', r: 0.9, opacity: 0.3, speed: 0.4 }] },
  // a Splash token: rings of water-blue spread from it now and then, reaching across its lane
  splash: {
    color: '#2f86d6',
    parts: [
      { t: 'aura', color: '#4fb8ff', r: 1.1, opacity: 0.3, speed: 0.5 },
      { t: 'ripple', color: '#7fd0ff', every: 2.6, r: 2.6, opacity: 0.5 },
    ],
  },
  // health a card has gained: pink, with a heart or two drifting up
  health: {
    color: '#e0409a',
    parts: [
      { t: 'aura', color: '#ff8fcb', r: 1.2, opacity: 0.3, grow: 0.7 },
      { t: 'motes', shape: 'heart', color: '#ffb3d9', count: 2, path: 'rise', life: 2.6, size: 0.6, spread: 0.6, height: 2, grow: 0.6 },
    ],
  },

  // plain markers with no effect of their own
  count: { color: '#46607a', parts: [] },
  // a skull: whatever it sits on is out of action, so the card under it is dimmed to a dull grey-black
  destroyed: { color: '#2e2f36', parts: [{ t: 'tint', color: '#0a0a0e', min: 0.52, max: 0.52 }] },
  // turn timers: only a faint glow, so the two can be told apart at a glance by color (gold = start of turn, violet = end of turn)
  'timer-start': { color: '#d9a21c', parts: [{ t: 'aura', color: '#ffd166', r: 0.85, opacity: 0.22, speed: 0.25 }] },
  'timer-end': { color: '#5b3fb0', parts: [{ t: 'aura', color: '#9a7dff', r: 0.85, opacity: 0.22, speed: 0.25 }] },
  // the timer that counts up (Arcane Charge): teal
  'timer-up': { color: '#1f9d8a', parts: [{ t: 'aura', color: '#6ff0d8', r: 0.85, opacity: 0.22, speed: 0.25 }] },

  // ---------------- damage counters (bigger with a higher number) ----------------
  range: {
    color: '#ffc857',
    parts: [
      { t: 'aura', color: '#ffc857', r: 1.5, opacity: 0.32, grow: 0.75 },
      { t: 'ripple', color: '#ffe2a0', every: 3.2, r: 2.6, opacity: 0.5, grow: 0.75 },
    ],
  },
  shield: {
    color: '#6cb6ff',
    parts: [
      { t: 'aura', color: '#6cb6ff', r: 1.5, opacity: 0.34, grow: 0.75 },
      { t: 'ripple', color: '#bfe0ff', every: 4, r: 2.2, opacity: 0.4, grow: 0.75 },
    ],
  },
  thorns: {
    color: '#7bd66f',
    parts: [
      { t: 'aura', color: '#7bd66f', r: 1.4, opacity: 0.3, grow: 0.75 },
      { t: 'motes', shape: 'spike', color: '#a6e89b', count: 8, path: 'out', life: 2.4, every: 2.4, size: 0.9, spread: 1.5, grow: 0.7 },
    ],
  },

  // ---------------- status effects ----------------
  bleed: {
    color: '#a01421',
    parts: [
      { t: 'aura', color: '#a01421', r: 0.8, opacity: 0.25, speed: 0.25 },
      { t: 'motes', shape: 'drop', color: '#c2182b', count: 3, path: 'out', life: 2.2, every: 4.5, size: 0.7, spread: 1.0, solid: true },
    ],
  },
  buffer: { color: '#8fd3ff', parts: [{ t: 'dome', color: '#8fd3ff', min: 0.06, max: 0.5 }] },
  burn: {
    color: '#ff7a1a',
    parts: [
      { t: 'aura', color: '#ff7a1a', color2: '#ffcf5a', r: 1.2, opacity: 0.65, speed: 1.6 },
      { t: 'motes', shape: 'dot', color: '#ff8a2a', color2: '#ffe08a', count: 9, path: 'rise', life: 1.3, size: 0.75, spread: 0.45, height: 2.8 },
    ],
  },
  comatose: {
    color: '#9aa7d6',
    parts: [{ t: 'motes', shape: 'z', color: '#e8ecff', count: 3, path: 'rise', life: 3.2, every: 6, size: 0.95, spread: 0.9, height: 3.4 }],
  },
  crumble: { color: '#9c8466', parts: [{ t: 'jitter', every: 6 }] },
  'damage-boost': {
    color: '#a25cff',
    parts: [
      { t: 'aura', color: '#a25cff', r: 1.3, opacity: 0.35, grow: 0.7 },
      { t: 'motes', shape: 'dot', color: '#c79bff', color2: '#7a3cff', count: 8, path: 'orbit', size: 0.5, spread: 1.15, speed: 1.6, trail: 2, grow: 0.7 },
    ],
  },
  // dark grey and black: a sooty shadow under the token, with grey ash and black specks drifting up off it
  decay: {
    color: '#3d3d43',
    parts: [
      { t: 'aura', color: '#1e1e22', color2: '#050506', r: 1.4, opacity: 0.6, speed: 0.3, grow: 0.7, solid: true },
      { t: 'motes', shape: 'dot', color: '#7a7a84', count: 5, path: 'rise', life: 3, size: 0.45, spread: 0.6, height: 1.6, solid: true, grow: 0.7 },
      { t: 'motes', shape: 'dot', color: '#0a0a0c', count: 3, path: 'rise', life: 3.4, size: 0.4, spread: 0.55, height: 1.4, solid: true, grow: 0.7 },
    ],
  },
  divinity: {
    color: '#ffe9a6',
    parts: [
      { t: 'halo', color: '#ffe08a' },
      { t: 'aura', color: '#fff3c4', r: 1.1, opacity: 0.3, speed: 0.25 },
    ],
  },
  frenzy: {
    color: '#b04cff',
    parts: [
      { t: 'aura', color: '#b04cff', r: 1.1, opacity: 0.35, speed: 1.2 },
      // a criss-cross: one quick slash across the card, then a second along the other diagonal, coming the
      // other way a moment later, so the two make an X. Smaller and faster than the Ambush slashes.
      { t: 'streaks', color: '#b04cff', color2: '#f0a8ff', every: 3.2, cross: true, scale: 0.72, speed: 1.6, gap: 0.14 },
    ],
  },
  frozen: {
    color: '#9fdcff',
    parts: [
      { t: 'tint', color: '#7cc8ff', min: 0.1, max: 0.45, overlay: 'frost' },
      { t: 'motes', shape: 'flake', color: '#ffffff', count: 2, path: 'rise', life: 3.5, every: 7, size: 0.7, spread: 1, height: 1.8 },
    ],
  },
  locked: { color: '#8a7a5a', parts: [] },
  overclock: {
    color: '#2ea8ff',
    parts: [
      { t: 'aura', color: '#2ea8ff', color2: '#ff3b3b', r: 1.5, opacity: 0.42, speed: 0.8, grow: 0.75 },
      { t: 'motes', shape: 'bolt', color: '#bfe6ff', color2: '#ff8a8a', count: 3, path: 'rise', life: 0.5, every: 3, size: 0.9, spread: 0.9, height: 0.6, grow: 0.5 },
    ],
  },
  overheal: {
    color: '#2ea8ff',
    parts: [
      { t: 'aura', color: '#2ea8ff', r: 1.4, opacity: 0.36, grow: 0.75 },
      { t: 'motes', shape: 'heart', color: '#ffd84a', count: 4, path: 'rise', life: 2.4, size: 0.75, spread: 0.7, height: 2.6, grow: 0.6 },
    ],
  },
  overshield: {
    color: '#2ea8ff',
    parts: [
      { t: 'aura', color: '#2ea8ff', color2: '#2a4bd6', r: 1.5, opacity: 0.42, speed: 0.5, grow: 0.75 },
      { t: 'ripple', color: '#4f7dff', every: 3.5, r: 2.3, opacity: 0.45, grow: 0.75 },
    ],
  },
  possession: {
    color: '#b9e6c2',
    parts: [
      { t: 'aura', color: '#b9e6c2', color2: '#8c949c', r: 1.2, opacity: 0.34, speed: 0.35 },
      { t: 'motes', shape: 'dot', color: '#cfeed6', color2: '#a0a8ad', count: 5, path: 'orbit', size: 0.45, spread: 1.05, speed: 0.8, trail: 3 },
    ],
  },
  regeneration: {
    color: '#ff8fb8',
    parts: [{ t: 'motes', shape: 'heart', color: '#ff9cc2', count: 3, path: 'rise', life: 2.8, size: 0.6, spread: 0.6, height: 2.4 }],
  },
  // amber palisade, with a steady warm glow and a slow ring settling outward
  entrenched: {
    color: '#b3771f',
    parts: [
      { t: 'aura', color: '#e0a548', r: 1.0, opacity: 0.34, speed: 0.25 },
      { t: 'ripple', color: '#f0cf8a', every: 7, r: 1.7, opacity: 0.38 },
    ],
  },
  stunned: {
    color: '#ffe45c',
    parts: [
      { t: 'arcs', color: '#fff3a0', every: 4 },
      { t: 'aura', color: '#ffe45c', r: 0.8, opacity: 0.25, speed: 2 },
    ],
  },
  // dark purple over the card, with pale cracks that show more as the number goes up
  // the blue counter for SP a Building has lost to decay (apart from the red damage counter)
  'sp-decay': { color: '#2f86e6', parts: [{ t: 'aura', color: '#4aa8ff', r: 0.9, opacity: 0.2, speed: 0.3 }] },
  susceptible: { color: '#3d1a66', parts: [{ t: 'tint', color: '#3d1a66', min: 0.12, max: 0.5, overlay: 'cracks', overlayColor: '#dcc8ff' }] },
  ambush: {
    color: '#9fe8ff',
    parts: [
      { t: 'aura', color: '#9fe8ff', color2: '#ffffff', r: 1.05, opacity: 0.38, speed: 0.7 },
      // (nothing circles the chip: the soft pulse above is its own, and the rest happens on the card)
      // the same slashes as when an Ambush card arrives, replayed every few seconds with a quick ring
      { t: 'streaks', color: '#ffffff', color2: '#9fe8ff', every: 6 },
      { t: 'ripple', color: '#9fe8ff', every: 6, r: 2.0, opacity: 0.5 },
    ],
  },
  // pastel purple over the card, with down arrows sinking onto the token (more arrows at higher numbers)
  weakness: {
    color: '#c9b3ff',
    parts: [
      { t: 'tint', color: '#c9b3ff', min: 0.1, max: 0.36 },
      { t: 'motes', shape: 'arrow', color: '#c9b3ff', count: 5, path: 'fall', life: 2.8, size: 1.1, spread: 0.85, height: 2.6, solid: true, grow: 0.6 },
    ],
  },
};
